import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { hasPermission, getRoutePermission } from '@/utils/permissions';
import { Card, CardContent } from '@/components/ui/card';
import { ShieldAlert } from 'lucide-react';

/**
 * ProtectedRoute Component
 * Checks if user has permission to access a route
 */
const ProtectedRoute = ({ user, children, requiredPermission, requiredRoles }) => {
  const location = useLocation();

  // Not logged in - redirect to login
  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Admin has access to everything
  if (user.role === 'Admin') {
    return children;
  }

  // Check role-based access
  if (requiredRoles && !requiredRoles.includes(user.role)) {
    return <AccessDenied reason="Your role doesn't have access to this page" />;
  }

  // Check permission-based access
  if (requiredPermission) {
    // Special: qualityAnalytics.any — allow if user has ANY QA tab permission
    if (requiredPermission === 'qualityAnalytics.any') {
      const qaPerm = user.module_permissions?.qualityAnalytics || user.permissions?.qualityAnalytics || {};
      const hasAnyQA = Object.values(qaPerm).some(v => v === true);
      if (!hasAnyQA) {
        return <AccessDenied reason="You don't have permission to access this page" />;
      }
    } else if (requiredPermission === 'calibration.view_any') {
      // Allow if user has either full Calibration section access OR just My Gauges access
      const hasAccess = hasPermission(user, 'calibration', 'view') || hasPermission(user, 'calibration', 'view_my_gauges');
      if (!hasAccess) {
        return <AccessDenied reason="You don't have permission to access this page" />;
      }
    } else {
      const [module, action] = requiredPermission.split('.');
      const hasAccess = hasPermission(user, module, action || 'view');
      if (!hasAccess) {
        return <AccessDenied reason="You don't have permission to access this page" />;
      }
    }
    // Explicit permission checked and passed — skip automatic route check
    return children;
  }

  // Check automatic permission based on route (fallback for routes without explicit requiredPermission)
  const routeModule = getRoutePermission(location.pathname);
  if (routeModule) {
    const hasAccess = hasPermission(user, routeModule, 'view');
    if (!hasAccess) {
      return <AccessDenied reason="You don't have permission to access this page" />;
    }
  }

  // All checks passed
  return children;
};

/**
 * AccessDenied Component
 * Shows when user doesn't have permission
 */
const AccessDenied = ({ reason }) => {
  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <Card className="max-w-md w-full">
        <CardContent className="pt-6">
          <div className="text-center">
            <ShieldAlert className="h-16 w-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-slate-800 mb-2">Access Denied</h2>
            <p className="text-slate-600 mb-4">
              {reason || "You don't have permission to access this page."}
            </p>
            <p className="text-sm text-slate-500">
              Please contact your administrator if you believe you should have access.
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ProtectedRoute;
